import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Paper, Chip, Stack, Typography, Box, Alert } from "@mui/material";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import TableViewTemplate from "./TableViewTemplate";
import { useSchoolNoticesQuery } from "../hooks/teacher/useTeacherOfflineQueries";
import { useOfflineSync } from "../providers/OfflineSyncProvider";

const PRIORITY_LABEL = { 0: "Normal", 1: "Medium", 2: "High", 3: "Urgent" };
const AUDIENCE_LABEL = { all: "All", teacher: "Teachers", student: "Students" };

function noticeVisibleForRole(notice, role) {
    const aud = String(notice.targetAudience || "all").toLowerCase();
    if (role === "Admin") return true;
    if (role === "Teacher") return aud === "all" || aud === "teacher";
    if (role === "Student") return aud === "all" || aud === "student";
    return true;
}

const SeeNotice = () => {
    const { currentUser, currentRole } = useSelector((state) => state.user);
    const { isOnline } = useOfflineSync();

    const schoolId = currentRole === "Admin" ? currentUser?._id : currentUser?.school?._id;

    const { data: noticesList = [], isLoading, isError, isFetching } = useSchoolNoticesQuery(schoolId);

    const filteredRows = useMemo(() => {
        if (!Array.isArray(noticesList)) return [];
        return noticesList
            .filter((n) => noticeVisibleForRole(n, currentRole))
            .map((notice) => {
                const date = new Date(notice.date);
                const dateString =
                    date.toString() !== "Invalid Date" ? date.toISOString().substring(0, 10) : "Invalid Date";
                const pr = notice.priority ?? 0;
                const aud = String(notice.targetAudience || "all").toLowerCase();
                return {
                    title: notice.title,
                    details: notice.details,
                    date: dateString,
                    audience: AUDIENCE_LABEL[aud] || aud,
                    priority: PRIORITY_LABEL[pr] ?? String(pr),
                    pinned: notice.pinned ? (
                        <Chip size="small" icon={<PushPinOutlinedIcon />} label="Pinned" color="primary" variant="outlined" />
                    ) : (
                        "—"
                    ),
                    id: notice._id,
                };
            });
    }, [noticesList, currentRole]);

    const noticeColumns = [
        { id: "pinned", label: "Pin", minWidth: 100 },
        { id: "priority", label: "Priority", minWidth: 90 },
        { id: "audience", label: "Audience", minWidth: 110 },
        { id: "title", label: "Title", minWidth: 160 },
        { id: "details", label: "Details", minWidth: 140 },
        { id: "date", label: "Date", minWidth: 110 },
    ];

    return (
        <Box sx={{ mt: { xs: 3, sm: 4 }, mr: { xs: 0, sm: 2 } }}>
            {!isOnline && (
                <Alert severity="info" icon={<CloudOffOutlinedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                    Showing saved notices — updates when you are back online.
                </Alert>
            )}

            {isLoading ? (
                <Typography>Loading...</Typography>
            ) : isError && !filteredRows.length ? (
                <Typography color="text.secondary">Could not load notices.</Typography>
            ) : filteredRows.length === 0 ? (
                <Typography color="text.secondary">No notices to show right now.</Typography>
            ) : (
                <>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Notices
                        </Typography>
                        {isFetching && !isLoading && (
                            <Typography variant="caption" color="text.secondary">
                                Updating…
                            </Typography>
                        )}
                    </Stack>
                    <Paper sx={{ width: "100%", overflow: "hidden" }}>
                        <TableViewTemplate columns={noticeColumns} rows={filteredRows} />
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default SeeNotice;
